import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Collapse,
  Button,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import MitraScreensBoard from './MitraScreensBoard';
import {
  OPERATE_FEATURE_SECTION_KEYS,
  OPERATE_FILTER_SECTION_KEYS,
} from './mitraScreensBoardUtils';

const BLOCK_HINTS = {
  ops_directory: 'Title + search bar',
  filter_chips: 'All / Bills / Products / … — hide chip → app falls back to All',
  store_overview: 'Catalog / Stock / Revenue card',
  feature_sections: 'Bills, Products, Leads rows (expand for card board)',
  account_settings: 'Plan / UPI / PIN — same edit · photo · hide · order as Bills',
  pos_shift_banner: 'Photo / copy / hide (still needs in-app Settings opt-in)',
  pos_terminal: 'LOKSY POS title / photo / hide',
  search: 'Grow search field',
  store_header: 'Grow store header',
  quick_access: 'Quick access / promo chips',
  business_info: 'Update business info section',
};

const BLOCK_CARD_SECTIONS = {
  feature_sections: OPERATE_FEATURE_SECTION_KEYS,
  account_settings: ['account_settings'],
  pos_shift_banner: ['pos_shell'],
  pos_terminal: ['pos_shell'],
};

function SortableBlock({
  block,
  index,
  total,
  expanded,
  onToggleExpand,
  onToggleVisible,
  onLabelChange,
  onMove,
  children,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: block.block_key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : block.is_visible ? 1 : 0.55,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 1.25,
        p: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <IconButton size="small" {...attributes} {...listeners} sx={{ cursor: 'grab' }}>
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Chip size="small" label={`${index + 1}`} sx={{ height: 22, minWidth: 28 }} />
        <TextField
          size="small"
          label="Block"
          value={block.label_en || ''}
          onChange={(e) => onLabelChange(block.block_key, e.target.value.slice(0, 120))}
          sx={{ flex: 1, minWidth: 140 }}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={block.is_visible !== false}
              onChange={() => onToggleVisible(block.block_key)}
            />
          }
          label={block.is_visible !== false ? 'On' : 'Off'}
          sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 12 } }}
        />
        <IconButton size="small" disabled={index === 0} onClick={() => onMove(block.block_key, -1)}>
          <ArrowUpwardIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          disabled={index >= total - 1}
          onClick={() => onMove(block.block_key, 1)}
        >
          <ArrowDownwardIcon fontSize="small" />
        </IconButton>
        {(block.block_key === 'filter_chips' ||
          BLOCK_CARD_SECTIONS[block.block_key]) && (
          <Tooltip title={expanded ? 'Collapse' : 'Expand'}>
            <IconButton size="small" onClick={() => onToggleExpand(block.block_key)}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" mt={0.5} ml={5}>
        <code>{block.block_key}</code>
        {BLOCK_HINTS[block.block_key] ? ` · ${BLOCK_HINTS[block.block_key]}` : ''}
      </Typography>
      {children}
    </Box>
  );
}

/**
 * Phone-style full-screen layout editor for one Mitra zone (operate | grow).
 */
export default function MitraFullScreenBoard({
  zone,
  blocks,
  layout,
  cards,
  onBlocksChange,
  onLayoutChange,
  onCardsChange,
  onEditCard,
  onToggleCardVisible,
}) {
  const [expanded, setExpanded] = useState({
    filter_chips: true,
    feature_sections: false,
    account_settings: true,
    pos_shift_banner: false,
    pos_terminal: false,
  });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const ordered = useMemo(
    () => [...(blocks || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [blocks],
  );

  const sections = layout?.sections?.[zone] || [];

  const renumber = (list) =>
    list.map((b, idx) => ({ ...b, sort_order: idx * 10 }));

  const updateBlocks = (next) => onBlocksChange(renumber(next));

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ordered.findIndex((b) => b.block_key === active.id);
    const newIndex = ordered.findIndex((b) => b.block_key === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    updateBlocks(arrayMove(ordered, oldIndex, newIndex));
  };

  const moveBy = (key, delta) => {
    const idx = ordered.findIndex((b) => b.block_key === key);
    if (idx < 0) return;
    const next = idx + delta;
    if (next < 0 || next >= ordered.length) return;
    updateBlocks(arrayMove(ordered, idx, next));
  };

  const toggleVisible = (key) => {
    updateBlocks(
      ordered.map((b) =>
        b.block_key === key ? { ...b, is_visible: !b.is_visible } : b,
      ),
    );
  };

  const onLabelChange = (key, label_en) => {
    updateBlocks(
      ordered.map((b) => (b.block_key === key ? { ...b, label_en } : b)),
    );
  };

  const toggleFilter = (sectionKey) => {
    const fixed = {
      ...layout.sections,
      [zone]: (layout.sections[zone] || []).map((s) =>
        s.section_key === sectionKey
          ? { ...s, show_in_filter: !(s.show_in_filter !== false) }
          : s,
      ),
    };
    onLayoutChange({ ...layout, sections: fixed });
  };

  return (
    <Box
      sx={{
        maxWidth: 480,
        mx: 'auto',
        p: 2,
        borderRadius: 3,
        border: '2px solid',
        borderColor: 'grey.300',
        bgcolor: 'grey.50',
        minHeight: 520,
      }}
    >
      <Typography variant="subtitle2" fontWeight={800} mb={1}>
        Full screen · {zone === 'operate' ? 'My Business Desk' : 'Connect with Loksy'}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={2}>
        Drag blocks or use ↑↓. Expand Account & Settings / POS / Feature rows for the same
        card controls as Hub (photo, languages, hide, reorder).
      </Typography>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext
          items={ordered.map((b) => b.block_key)}
          strategy={verticalListSortingStrategy}
        >
          {ordered.map((block, index) => (
            <SortableBlock
              key={block.block_key}
              block={block}
              index={index}
              total={ordered.length}
              expanded={Boolean(expanded[block.block_key])}
              onToggleExpand={(k) =>
                setExpanded((prev) => ({ ...prev, [k]: !prev[k] }))
              }
              onToggleVisible={toggleVisible}
              onLabelChange={onLabelChange}
              onMove={moveBy}
            >
              {block.block_key === 'filter_chips' && expanded.filter_chips ? (
                <Collapse in>
                  <Box mt={1.5} ml={1} p={1.5} bgcolor="white" borderRadius={2} border="1px solid" borderColor="divider">
                    <Typography variant="caption" fontWeight={700} display="block" mb={1}>
                      Filter chips (All always shown). Off → hidden in Mitra; active filter resets to All.
                    </Typography>
                    <Chip size="small" label="All (always on)" color="primary" sx={{ mr: 0.75, mb: 0.75 }} />
                    {sections
                      .filter(
                        (s) =>
                          zone !== 'operate' ||
                          OPERATE_FILTER_SECTION_KEYS.includes(s.section_key),
                      )
                      .map((s) => (
                      <Chip
                        key={s.section_key}
                        size="small"
                        label={s.label_en || s.section_key}
                        color={s.show_in_filter !== false ? 'default' : 'default'}
                        variant={s.show_in_filter !== false ? 'outlined' : 'filled'}
                        onClick={() => toggleFilter(s.section_key)}
                        sx={{ mr: 0.75, mb: 0.75, opacity: s.show_in_filter !== false ? 1 : 0.55 }}
                      />
                    ))}
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                      Click a chip to toggle filter visibility.
                    </Typography>
                  </Box>
                </Collapse>
              ) : null}

              {BLOCK_CARD_SECTIONS[block.block_key] &&
              expanded[block.block_key] ? (
                <Collapse in>
                  <Box mt={1.5} sx={{ maxHeight: 480, overflow: 'auto' }}>
                    <Typography variant="caption" fontWeight={700} display="block" mb={1} ml={1}>
                      Cards: edit picture · EN/TE/HI · hide · ←→ order (same as Hub cards)
                    </Typography>
                    <MitraScreensBoard
                      zone={zone}
                      cards={
                        block.block_key === 'pos_shift_banner'
                          ? cards.filter((c) => c.card_key === 'op_pos_shift_banner')
                          : block.block_key === 'pos_terminal'
                            ? cards.filter((c) => c.card_key === 'op_pos_terminal')
                            : cards
                      }
                      layout={layout}
                      onlySections={BLOCK_CARD_SECTIONS[block.block_key]}
                      onCardsChange={onCardsChange}
                      onLayoutChange={onLayoutChange}
                      onEditCard={onEditCard}
                      onToggleVisible={onToggleCardVisible}
                    />
                  </Box>
                </Collapse>
              ) : null}

              {BLOCK_CARD_SECTIONS[block.block_key] &&
              !expanded[block.block_key] ? (
                <Button
                  size="small"
                  sx={{ mt: 1, ml: 5 }}
                  onClick={() =>
                    setExpanded((p) => ({ ...p, [block.block_key]: true }))
                  }
                >
                  Expand card board
                </Button>
              ) : null}
            </SortableBlock>
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  );
}
